import { useQueryClient } from '@tanstack/react-query';
import {
    useGetAllProductProperties,
    useGetAllActiveProductProperties,
    useGetProductProperties,
    useCreateProductProperties as useCreateProductPropertiesApi,
    useUpdateProductProperties as useUpdateProductPropertiesApi,
    useUpdateProductStatus as useUpdateProductStatusApi,
    useUpdateProductOnlineStatus as useUpdateProductOnlineStatusApi
} from '@/shared/api/client/product-properties-resource';
import { showErrorNotification, showSuccessNotification } from '@/components/common/utils/notify';
import createParamsSerializer from '@/shared/utils/create-params-serializer';

// Interface for pagination parameters
interface PaginationParams {
    page?: number;
    size?: number;
    sort?: string;
}

// Get all product properties paginated
export const useProductProperties = (params: PaginationParams = {}) => {
    const { page = 0, size = 10, sort } = params;

    return useGetAllProductProperties({
        pageable: {
            page,
            size,
            sort: sort ? [sort] : undefined
        }
    });
};

// Get active product properties
export const useActiveProductProperties = () => {
    return useGetAllActiveProductProperties({});
};

// Get a single product property by ID
export const useProductPropertiesById = (glNo: string | undefined) => {
    return useGetProductProperties(glNo!, {
        query: {
            enabled: !!glNo
        }
    });
};

// Create a new product property
export const useCreateProductProperties = () => {
    const queryClient = useQueryClient();

    return useCreateProductPropertiesApi({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['product-properties'] });
                showSuccessNotification('Product properties created successfully');
            },
            onError: (error: any) => {
                showErrorNotification(
                    'Failed to create product properties',
                    error.response?.data?.message || 'An error occurred'
                );
            }
        }
    });
};

// Update product properties
export const useUpdateProductProperties = () => {
    const queryClient = useQueryClient();

    return useUpdateProductPropertiesApi({
        mutation: {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries({ queryKey: ['product-properties'] });
                queryClient.invalidateQueries({ queryKey: ['product-properties', variables.glNo] });
                showSuccessNotification('Product properties updated successfully');
            },
            onError: (error: any) => {
                showErrorNotification(
                    'Failed to update product properties',
                    error.response?.data?.message || 'An error occurred'
                );
            }
        }
    });
};

// Update product status (active/inactive)
export const useUpdateProductStatus = () => {
    const queryClient = useQueryClient();

    return useUpdateProductStatusApi({
        mutation: {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries({ queryKey: ['product-properties'] });
                queryClient.invalidateQueries({ queryKey: ['product-properties', variables.glNo] });
                showSuccessNotification(`Product ${variables.params.isActive ? 'activated' : 'deactivated'} successfully`);
            },
            onError: (error: any) => {
                showErrorNotification(
                    'Failed to update product status',
                    error.response?.data?.message || 'An error occurred'
                );
            }
        }
    });
};

// Update product online status
export const useUpdateProductOnlineStatus = () => {
    const queryClient = useQueryClient();

    return useUpdateProductOnlineStatusApi({
        mutation: {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries({ queryKey: ['product-properties'] });
                queryClient.invalidateQueries({ queryKey: ['product-properties', variables.glNo] });
                showSuccessNotification(`Product is now ${variables.params.online ? 'available' : 'unavailable'} online`);
            },
            onError: (error: any) => {
                showErrorNotification(
                    'Failed to update product online status',
                    error.response?.data?.message || 'An error occurred'
                );
            }
        }
    });
};